import { memo, useMemo } from "react";
import PropTypes from "prop-types";

import { Box, Divider, Tooltip, Typography, useTheme } from "@mui/material";

const SoOrderInfoTooltip = ({ orderInfo, children }) => {
  const theme = useTheme();

  const rows = useMemo(
    () => [
      { label: "Buyer name", value: orderInfo?.buyerName || "-" },
      { label: "Seller name", value: orderInfo?.sellerName || "-" },
      { label: "Buyer GSTIN", value: orderInfo?.buyerGstin || "-" },
      { label: "Seller GSTIN", value: orderInfo?.sellerGstin || "-" },
    ],
    [orderInfo],
  );

  return (
    <Tooltip
      arrow
      placement="bottom-start"
      enterDelay={120}
      title={
        <Box sx={{ width: 380, maxWidth: "calc(100vw - 64px)", p: 2.5 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              mb: 1.5,
              color: theme.palette.text.primary,
              textAlign: "center",
            }}
          >
            {orderInfo?.orderNo}
          </Typography>
          <Divider sx={{ mb: 1.5 }} />

          {rows.map((row, idx) => (
            <Box key={row.label}>
              <Box display="flex" alignItems="flex-start" mb={1.75}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    minWidth: 110,
                    pr: 2,
                  }}
                >
                  {row.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 700,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    flex: 1,
                  }}
                >
                  {row.value}
                </Typography>
              </Box>

              {idx !== rows.length - 1 && <Divider sx={{ mb: 1.75 }} />}
            </Box>
          ))}
        </Box>
      }
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: "none",
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[200]}`,
            boxShadow: "0px 8px 30px rgba(0,0,0,0.12)",
            p: 0,
            overflow: "hidden",
          },
        },
        arrow: {
          sx: {
            color: theme.palette.background.paper,
          },
        },
      }}
    >
      <Box component="span">{children}</Box>
    </Tooltip>
  );
};

SoOrderInfoTooltip.propTypes = {
  orderInfo: PropTypes.shape({
    orderNo: PropTypes.string,
    buyerName: PropTypes.string,
    sellerName: PropTypes.string,
    buyerGstin: PropTypes.string,
    sellerGstin: PropTypes.string,
  }).isRequired,
  children: PropTypes.node.isRequired,
};

export default memo(SoOrderInfoTooltip);
